import React, { Suspense } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { ArrowRight } from 'lucide-react';
import { m } from 'framer-motion';
import { useLoading } from '../../context/LoadingContext';

const ThreeBackground = React.lazy(() =>
  import('../ui/ThreeBackground').then((module) => ({ default: module.ThreeBackground }))
);

export const Hero: React.FC = () => {
  const { t, dir } = useLanguage();
  const { isLoading, setThreeReady } = useLoading();
  const [isDesktop, setIsDesktop] = React.useState(false);

  React.useEffect(() => {
    const checkDesktop = () => {
      const isLarge = window.innerWidth >= 768;
      setIsDesktop(isLarge);
      if (!isLarge) {
        setThreeReady(true);
      }
    };
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, [setThreeReady]);

  const handleScrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-rs-bg"
    >
      <Suspense fallback={<div className="absolute inset-0 bg-rs-bg" />}>
        <ThreeBackground />
      </Suspense>

      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-rs-muted/20 rounded-full blur-[128px] animate-pulse"></div>
      </div>

      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto pointer-events-none">
        <m.h2
          initial={{ opacity: 0, y: -20 }}
          animate={!isLoading ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-rs-bright font-medium tracking-widest text-sm md:text-base mb-4 uppercase"
        >
          {t('hero.est')}
        </m.h2>

        <m.h1
          initial={{ opacity: 0, scale: 0.9 }}
          animate={!isLoading ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.4, type: 'spring' }}
          className="text-3xl md:text-8xl font-bold text-white mb-6 tracking-tight leading-tight drop-shadow-[0_0_15px_rgba(231,10,53,0.5)]"
        >
          {t('hero.brand')}
        </m.h1>

        <m.p
          initial={{ opacity: 0, y: 20 }}
          animate={!isLoading ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-xl md:text-2xl text-gray-300 mb-10 font-light max-w-2xl mx-auto"
        >
          {t('hero.subtitle')}
        </m.p>

        <m.div
          initial={{ opacity: 0, y: 30 }}
          animate={!isLoading ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center pointer-events-auto"
        >
          <button
            onClick={() => handleScrollTo('projects')}
            className="group relative px-8 py-3 bg-rs-bright text-white font-bold rounded-none overflow-hidden transition-all hover:bg-rs-muted cursor-pointer"
          >
            <span className="relative z-10 flex items-center gap-2">
              {t('hero.cta')}
              {dir === 'ltr' ? (
                <ArrowRight size={18} />
              ) : (
                <ArrowRight size={18} className="rotate-180" />
              )}
            </span>
            <div className="absolute inset-0 h-full w-full scale-0 rounded-none transition-all duration-300 group-hover:scale-100 group-hover:bg-rs-maroon/20"></div>
          </button>

          <button
            onClick={() => handleScrollTo('contact')}
            className="px-8 py-3 border border-rs-muted text-rs-muted hover:text-white hover:bg-rs-muted/20 transition-all font-semibold cursor-pointer"
          >
            {t('section.contact')}
          </button>
        </m.div>
      </div>
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 pointer-events-none mix-blend-overlay"></div>
    </section>
  );
};
